/**
 * Orca v3 — a run of a loop, the event log it writes, and its review, as stored in
 * `orcaloops_v3.runs`, `orcaloops_v3.run_events` and `orcaloops_v3.run_reviews`.
 *
 * Every step a run takes is one {@link V3RunEvent}, appended by the loop host in `seq`
 * order and never rewritten. The run page is derived from the event log alone, so the log
 * is the contract between the host, the api and the web. `@fm/v3` re-exports these
 * schemas without the `V3` prefix.
 *
 * **Order.** `run.started` is first. `run.finished` is the last event of the work; the
 * review follows it, so `run.reviewed` (when the run is reviewed at all) is the very last.
 *
 * **Cost.** A run's cost is the sum of its `agent.finished.costUsd`. Every agent that costs
 * money emits one — the orchestrator, subagents, reviewers, the input check's judge and
 * the evaluator alike. `run.finished.costUsd` is that sum when the work ended (before the
 * review); the run row's `cost_usd` keeps summing, the evaluator's included.
 * `run.reviewed.costUsd` repeats the evaluator's cost for display and is never added.
 *
 * @since 1.0.0
 */
import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { V3Params } from "./V3Loop.ts";

/**
 * Where a run stands.
 *
 * - `queued` — row written, host not yet dispatched.
 * - `starting` — host container dispatched, not yet reporting.
 * - `running` — the host is working.
 * - `blocked` — the input check found a blocker; nothing ran.
 * - `met` — every condition held and every required after-action succeeded.
 * - `stopped` — a person stopped it; work was kept.
 * - `failed` — the host could not continue.
 * - `out_of_budget` — the money or time ceiling was reached; work was kept.
 *
 * @since 1.0.0
 */
export const V3RunStatus = Schema.Literal(
  "queued",
  "starting",
  "running",
  "blocked",
  "met",
  "stopped",
  "failed",
  "out_of_budget",
);
export type V3RunStatus = typeof V3RunStatus.Type;

/**
 * Statuses after which a run never changes again.
 *
 * @since 1.0.0
 */
export const V3_TERMINAL_RUN_STATUSES: ReadonlyArray<V3RunStatus> = [
  "blocked",
  "met",
  "stopped",
  "failed",
  "out_of_budget",
];

/**
 * A condition's verdict on one subject.
 *
 * @since 1.0.0
 */
export const V3ConditionState = Schema.Literal("met", "unmet", "pending");
export type V3ConditionState = typeof V3ConditionState.Type;

/**
 * One fact a verdict rests on: which system said what, and when.
 *
 * @since 1.0.0
 */
export const V3Evidence = Schema.Struct({
  source: Schema.String,
  field: Schema.String,
  value: Schema.Unknown,
  url: Schema.optional(Schema.String),
  observedAt: Schema.String,
});
export type V3Evidence = typeof V3Evidence.Type;

/**
 * One problem a check or review names.
 *
 * @since 1.0.0
 */
export const V3Finding = Schema.Struct({
  text: Schema.String,
  file: Schema.optional(Schema.String),
  line: Schema.optional(Schema.Number),
  severity: Schema.optional(Schema.Literal("blocker", "warn", "note")),
});
export type V3Finding = typeof V3Finding.Type;

/**
 * A condition's result on one subject (a commit sha, a change request, an issue).
 *
 * `terminal` means the result cannot change for this subject: a failing check on a commit
 * stays failing; CI still running is `pending` and not terminal.
 *
 * @since 1.0.0
 */
export const V3ConditionResult = Schema.Struct({
  state: V3ConditionState,
  terminal: Schema.Boolean,
  subject: Schema.String,
  evidence: Schema.Array(V3Evidence),
  findings: Schema.optional(Schema.Array(V3Finding)),
  /** A short human line: "3 findings", "merges cleanly into main", a failing part's name. */
  detail: Schema.optional(Schema.String),
  /** A log tail when the result is a failing command. */
  log: Schema.optional(Schema.String),
  ms: Schema.Number,
  costUsd: Schema.optional(Schema.Number),
  /** True when the result was reused from an earlier check of identical content. */
  reused: Schema.optional(Schema.Boolean),
  /**
   * `environment` when the host stopped the work (out of memory, SIGKILL, a time limit):
   * not a verdict on the subject.
   */
  cause: Schema.optional(Schema.Literal("environment")),
});
export type V3ConditionResult = typeof V3ConditionResult.Type;

/**
 * What happened to a subagent's work when it finished.
 *
 * @since 1.0.0
 */
export const V3Landing = Schema.Struct({
  landed: Schema.Boolean,
  /** The change branch head after landing. */
  sha: Schema.optional(Schema.String),
  /** The branch the work was kept on when it did not land. */
  kept: Schema.optional(Schema.String),
  conflicts: Schema.optional(Schema.Array(Schema.String)),
  commits: Schema.optional(Schema.Number),
  note: Schema.String,
});
export type V3Landing = typeof V3Landing.Type;

/**
 * One gap the input check found before the run started.
 *
 * @since 1.0.0
 */
export const V3InputFinding = Schema.Struct({
  quote: Schema.String,
  kind: Schema.String,
  severity: Schema.Literal("blocker", "warn"),
  why: Schema.String,
  needed: Schema.String,
  /** Whether the fix belongs in the inputs or in the outcome definition. */
  fixIn: Schema.Literal("inputs", "outcome"),
});
export type V3InputFinding = typeof V3InputFinding.Type;

/**
 * An agent's part in the run.
 *
 * @since 1.0.0
 */
export const V3AgentRole = Schema.Literal("orchestrator", "subagent", "reviewer", "evaluator");
export type V3AgentRole = typeof V3AgentRole.Type;

/**
 * When an action ran.
 *
 * @since 1.0.0
 */
export const V3ActionPhase = Schema.Literal("before", "during", "after", "if_unmet");
export type V3ActionPhase = typeof V3ActionPhase.Type;

const base = {
  runId: Schema.String,
  /** 1..n, gap-free per run, assigned by the host's shipper. */
  seq: Schema.Number,
  /** ISO-8601 instant the host observed the event. */
  at: Schema.String,
};

/**
 * @since 1.0.0
 */
export const V3RunStarted = Schema.Struct({
  ...base,
  kind: Schema.Literal("run.started"),
  loopId: Schema.String,
  loopVersion: Schema.Number,
  number: Schema.Number,
  /** Every input as the run saw it, connector inputs snapshotted. */
  inputs: V3Params,
  host: Schema.Struct({ runner: Schema.optional(Schema.String), image: Schema.optional(Schema.String) }),
  resumedFrom: Schema.optional(Schema.String),
});

/**
 * @since 1.0.0
 */
export const V3RunSetup = Schema.Struct({
  ...base,
  kind: Schema.Literal("run.setup"),
  phase: Schema.Literal("inputs", "seed", "install", "image", "proxy"),
  ok: Schema.Boolean,
  ms: Schema.Number,
  detail: Schema.optional(Schema.String),
});

/**
 * @since 1.0.0
 */
export const V3InputCheckFinished = Schema.Struct({
  ...base,
  kind: Schema.Literal("input_check.finished"),
  verdict: Schema.Literal("clear", "warnings", "blocked"),
  findings: Schema.Array(V3InputFinding),
  ms: Schema.Number,
  /** Shown beside the check; already counted in the run's cost by its judge's `agent.finished`. */
  costUsd: Schema.Number,
});

/**
 * @since 1.0.0
 */
export const V3AgentStarted = Schema.Struct({
  ...base,
  kind: Schema.Literal("agent.started"),
  agentId: Schema.String,
  role: V3AgentRole,
  title: Schema.String,
  /** The brief the agent was given. */
  task: Schema.optional(Schema.String),
  model: Schema.optional(Schema.String),
  /** Condition ids whose failure this agent was sent to fix. */
  forConditions: Schema.optional(Schema.Array(Schema.String)),
  continuedFrom: Schema.optional(Schema.String),
  fromBranch: Schema.optional(Schema.String),
});

/**
 * The agent's own words, condensed.
 *
 * @since 1.0.0
 */
export const V3AgentSaid = Schema.Struct({
  ...base,
  kind: Schema.Literal("agent.said"),
  agentId: Schema.String,
  text: Schema.String,
});

/**
 * One tool use by an agent, condensed to a gist ("read 3 files in packages/api").
 *
 * @since 1.0.0
 */
export const V3AgentTool = Schema.Struct({
  ...base,
  kind: Schema.Literal("agent.tool"),
  agentId: Schema.String,
  tool: Schema.String,
  gist: Schema.String,
  ms: Schema.optional(Schema.Number),
});

/**
 * @since 1.0.0
 */
export const V3AgentFinished = Schema.Struct({
  ...base,
  kind: Schema.Literal("agent.finished"),
  agentId: Schema.String,
  ms: Schema.Number,
  costUsd: Schema.Number,
  turns: Schema.Number,
  inputTokens: Schema.Number,
  outputTokens: Schema.Number,
  isError: Schema.Boolean,
  summary: Schema.optional(Schema.String),
  landing: Schema.optional(V3Landing),
});

/**
 * One orchestrator tool call — a timed step.
 *
 * @since 1.0.0
 */
export const V3Step = Schema.Struct({
  ...base,
  kind: Schema.Literal("step"),
  tool: Schema.String,
  args: Schema.Unknown,
  ms: Schema.Number,
  result: Schema.Unknown,
  refused: Schema.optional(Schema.String),
});

/**
 * A condition began to be decided on a subject — so the page can draw the check as a bar
 * from here to its `condition.checked`.
 *
 * @since 1.0.0
 */
export const V3ConditionStarted = Schema.Struct({
  ...base,
  kind: Schema.Literal("condition.started"),
  conditionId: Schema.String,
  subject: Schema.String,
  /** The check parts being run, when only some are. */
  parts: Schema.optional(Schema.Array(Schema.String)),
});

/**
 * @since 1.0.0
 */
export const V3ConditionChecked = Schema.Struct({
  ...base,
  kind: Schema.Literal("condition.checked"),
  conditionId: Schema.String,
  result: V3ConditionResult,
});

/**
 * @since 1.0.0
 */
export const V3ActionTaken = Schema.Struct({
  ...base,
  kind: Schema.Literal("action.taken"),
  phase: V3ActionPhase,
  connector: Schema.String,
  action: Schema.String,
  required: Schema.Boolean,
  ok: Schema.Boolean,
  /** Whether a re-read of the system confirmed the action; absent when it has no verify. */
  verified: Schema.optional(Schema.Boolean),
  evidence: Schema.Array(V3Evidence),
  /** What the action produced, e.g. a change request's id and url. */
  artifact: Schema.optional(
    Schema.Struct({ kind: Schema.String, id: Schema.String, url: Schema.optional(Schema.String) }),
  ),
  detail: Schema.optional(Schema.String),
  ms: Schema.Number,
});

/**
 * Where an outcome stands: produced by the run, verified by a re-read, or replaced by a
 * later outcome.
 *
 * @since 1.0.0
 */
export const V3OutcomeStatus = Schema.Literal("produced", "verified", "superseded");
export type V3OutcomeStatus = typeof V3OutcomeStatus.Type;

/**
 * One stored file of an outcome; `path` is relative to the outcome.
 *
 * @since 1.0.0
 */
export const V3OutcomeFile = Schema.Struct({
  path: Schema.String,
  bytes: Schema.Number,
  sha256: Schema.String,
  contentType: Schema.String,
});
export type V3OutcomeFile = typeof V3OutcomeFile.Type;

/**
 * Something a run produced: a change request, a commit, a design, a document, …
 *
 * @since 1.0.0
 */
export const V3Outcome = Schema.Struct({
  /** A ulid. */
  id: Schema.String,
  /** Open: change_request, commit, design, document, … */
  kind: Schema.String,
  title: Schema.String,
  /** Where the outcome lives outside the run, e.g. a change request's connector, url and id. */
  ref: Schema.optional(Schema.Struct({
    connector: Schema.optional(Schema.String),
    url: Schema.optional(Schema.String),
    id: Schema.optional(Schema.String),
  })),
  status: V3OutcomeStatus,
  by: Schema.Literal("agent", "runtime"),
  files: Schema.Array(V3OutcomeFile),
});
export type V3Outcome = typeof V3Outcome.Type;

/**
 * Recorded, or recorded again with a new status or files: the latest record of an id wins.
 *
 * @since 1.0.0
 */
export const V3OutcomeRecorded = Schema.Struct({
  ...base,
  kind: Schema.Literal("outcome.recorded"),
  outcome: V3Outcome,
});

/**
 * @since 1.0.0
 */
export const V3PersonAsked = Schema.Struct({
  ...base,
  kind: Schema.Literal("person.asked"),
  question: Schema.String,
  answer: Schema.optional(Schema.String),
  answeredBy: Schema.optional(Schema.String),
});

/**
 * @since 1.0.0
 */
export const V3RunFinished = Schema.Struct({
  ...base,
  kind: Schema.Literal("run.finished"),
  status: V3RunStatus,
  summary: Schema.optional(Schema.String),
  ms: Schema.Number,
  /** The sum of every `agent.finished.costUsd` so far — the evaluator's, which comes later, excluded. */
  costUsd: Schema.Number,
  /** The run's change branch on the workspace remote (`v3/<runId>/change`), when it has one. */
  changeBranch: Schema.optional(Schema.String),
  /** Unlanded work kept on `v3/<runId>/wip/<agentId>` branches — what a resume starts from. */
  kept: Schema.Array(Schema.Struct({ agentId: Schema.String, branch: Schema.String })),
});

/**
 * A rating on one scorecard area.
 *
 * @since 1.0.0
 */
export const V3Rating = Schema.Literal("good", "watch", "problem", "not measured");
export type V3Rating = typeof V3Rating.Type;

/**
 * The six areas every run review covers.
 *
 * @since 1.0.0
 */
export const V3ScoreAreas = ["time", "memory", "disk", "agentCalls", "checks", "reliability"] as const;
export type V3ScoreArea = (typeof V3ScoreAreas)[number];

/**
 * The fixed scorecard, written by the runtime from its own measurements alone — one plain
 * line per area ("31.2 min, 4.1 of it setup"). It needs no evaluator, so every run gets one,
 * a stopped run included.
 *
 * @since 1.0.0
 */
export const V3Scorecard = Schema.Struct({
  time: Schema.String,
  memory: Schema.String,
  disk: Schema.String,
  agentCalls: Schema.String,
  checks: Schema.String,
  reliability: Schema.String,
});
export type V3Scorecard = typeof V3Scorecard.Type;

/**
 * A rating of one area, with the evaluator's reason.
 *
 * @since 1.0.0
 */
export const V3AreaRating = Schema.Struct({ rating: V3Rating, note: Schema.String });
export type V3AreaRating = typeof V3AreaRating.Type;

/**
 * One ranked improvement, tagged with where it would live and what it would save.
 *
 * @since 1.0.0
 */
export const V3Improvement = Schema.Struct({
  change: Schema.String,
  where: Schema.Literal("playbook", "runtime", "checks", "repo tooling", "loop definition"),
  saves: Schema.optional(Schema.Struct({
    minutes: Schema.optional(Schema.Number),
    usd: Schema.optional(Schema.Number),
  })),
  evidence: Schema.String,
});
export type V3Improvement = typeof V3Improvement.Type;

/**
 * A fresh evaluator's reading of the facts: a rating per area, what worked, what didn't,
 * and improvements ranked by what they would save.
 *
 * @since 1.0.0
 */
export const V3Assessment = Schema.Struct({
  summary: Schema.String,
  ratings: Schema.Struct({
    time: V3AreaRating,
    memory: V3AreaRating,
    disk: V3AreaRating,
    agentCalls: V3AreaRating,
    checks: V3AreaRating,
    reliability: V3AreaRating,
  }),
  worked: Schema.Array(Schema.String),
  didnt: Schema.Array(Schema.String),
  improvements: Schema.Array(V3Improvement),
});
export type V3Assessment = typeof V3Assessment.Type;

/**
 * A run's review. The scorecard is always there; the assessment is there unless the
 * evaluator was skipped — a stopped run gets facts and a scorecard only
 * (`skipped: "stopped"`), because its stop path has 100 s in all.
 *
 * @since 1.0.0
 */
export const V3RunReviewBody = Schema.Struct({
  scorecard: V3Scorecard,
  assessment: Schema.optional(V3Assessment),
  skipped: Schema.optional(Schema.Literal("stopped")),
});
export type V3RunReviewBody = typeof V3RunReviewBody.Type;

/**
 * The measured facts a review rests on. Every number comes from the runtime, never from
 * an agent's own account.
 *
 * @since 1.0.0
 */
export const V3RunFacts = Schema.Struct({
  wallMs: Schema.Number,
  costUsd: Schema.Number,
  /** Per part of the run: setup, input check, orchestrator, subagents, checks, reviews, actions. */
  partsMs: Schema.Record({ key: Schema.String, value: Schema.Number }),
  agents: Schema.Array(Schema.Struct({
    agentId: Schema.String,
    role: V3AgentRole,
    ms: Schema.Number,
    modelMs: Schema.Number,
    commandMs: Schema.Number,
    costUsd: Schema.Number,
    turns: Schema.Number,
  })),
  checks: Schema.Struct({ runs: Schema.Number, reused: Schema.Number, failedFirst: Schema.Number, ms: Schema.Number }),
  peakMemoryBytes: Schema.Record({ key: Schema.String, value: Schema.Number }),
  diskMb: Schema.Record({ key: Schema.String, value: Schema.Number }),
});
export type V3RunFacts = typeof V3RunFacts.Type;

/**
 * @since 1.0.0
 */
export const V3RunReviewed = Schema.Struct({
  ...base,
  kind: Schema.Literal("run.reviewed"),
  facts: V3RunFacts,
  review: V3RunReviewBody,
  /** The evaluator's cost, for display only — already counted by its `agent.finished`; 0 when skipped. */
  costUsd: Schema.Number,
});

/**
 * Every event a run writes. `kind` is the discriminator.
 *
 * @since 1.0.0
 */
export const V3RunEvent = Schema.Union(
  V3RunStarted,
  V3RunSetup,
  V3InputCheckFinished,
  V3AgentStarted,
  V3AgentSaid,
  V3AgentTool,
  V3AgentFinished,
  V3Step,
  V3ConditionStarted,
  V3ConditionChecked,
  V3ActionTaken,
  V3OutcomeRecorded,
  V3PersonAsked,
  V3RunFinished,
  V3RunReviewed,
);
export type V3RunEvent = typeof V3RunEvent.Type;

/**
 * Every event kind, in the order a run usually writes them.
 *
 * @since 1.0.0
 */
export type V3RunEventKind = V3RunEvent["kind"];

/**
 * A run row.
 *
 * @since 1.0.0
 */
export class V3Run extends Model.Class<V3Run>("V3Run")({
  id: Model.Generated(Schema.String),
  customerId: Schema.String,
  loopId: Schema.String,
  loopVersion: Schema.Number,
  /** 1..n within the loop: "Run 4". */
  number: Schema.Number,
  title: Schema.String,
  status: V3RunStatus,
  inputs: V3Params,
  createdByPersonId: Schema.String,
  runnerId: Schema.NullOr(Schema.String),
  hostContainer: Schema.NullOr(Schema.String),
  resumedFromRunId: Schema.NullOr(Schema.String),
  /** The sum of every `agent.finished.costUsd`, the evaluator's included (see the module doc). */
  costUsd: Schema.Number,
  lastSeq: Schema.Number,
  /** How many of the definition's conditions held when last checked, for list rows. */
  conditionsMet: Schema.Number,
  conditionsTotal: Schema.Number,
  /** Queue priority: 1 Urgent … 4 Low, 0 none (sorts after 4). */
  priority: Schema.Number,
  /** The manual queue rank; `null` sorts as `created_at` in whole epoch ms. */
  queueRank: Schema.NullOr(Schema.Number),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
  startedAt: Schema.NullOr(Schema.DateTimeUtc),
  endedAt: Schema.NullOr(Schema.DateTimeUtc),
  /** The proposal the run started from, if any, and the exact revision of it. */
  proposalId: Schema.NullOr(Schema.String),
  proposalRevision: Schema.NullOr(Schema.Number),
}) {}
